// Agent Chat Panel - Main UI component for the AI chat interface

import { useRef, useEffect, useState, useCallback } from 'react';
import { useAgent } from './agentContextCore';
import { MessageBubble } from './components/MessageBubble';
import { PipelineStatusIndicator } from './components/PipelineStatusIndicator';
import { V2PipelineTreeView } from './components/V2PipelineTreeView';
import { AgentToolTimeline } from './components/AgentToolTimeline';
import { IconChat } from './components/icons';
import './AgentChat.css';

export function AgentChatPanel() {
  const { state, sendMessage, clearHistory, revertCheckpoint, updateSettings } = useAgent();
  const [inputValue, setInputValue] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [showCheckpoints, setShowCheckpoints] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const revertableCheckpoints = state.checkpoints.filter(cp => !cp.reverted);

  const { pipelineStatus } = state;
  const showPipelineStatus = state.isProcessing && pipelineStatus.step !== 'idle';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, pipelineStatus]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
  }, []);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputValue.trim() || state.isProcessing) return;

    const message = inputValue;
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    await sendMessage(message);
  }, [inputValue, state.isProcessing, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  return (
    <div className="agent-chat-panel" data-testid="agent-chat-panel">
      {/* Header */}
      <div className="agent-chat-header" data-testid="agent-chat-header">
        <div className="agent-chat-title">
          <img className="agent-icon" src="/gottibot.png" alt="Gotti bot" />
          <span>Agent</span>
          <span
            className={`agent-status ${state.isConnected ? 'connected' : 'disconnected'}`}
            data-testid="agent-connection-status"
            data-connected={state.isConnected}
          >
            {state.isConnected ? '●' : '○'}
          </span>
        </div>
        <div className="agent-chat-actions">
          <button
            className={`agent-action-btn ${revertableCheckpoints.length > 0 ? 'has-checkpoints' : ''}`}
            onClick={() => setShowCheckpoints(!showCheckpoints)}
            title={revertableCheckpoints.length > 0
              ? `${revertableCheckpoints.length} change(s) can be undone`
              : 'No changes to undo'}
            disabled={state.checkpoints.length === 0}
            data-testid="agent-checkpoints-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
            {revertableCheckpoints.length > 0 && (
              <span className="checkpoint-badge">{revertableCheckpoints.length}</span>
            )}
          </button>
          <button
            className="agent-action-btn"
            onClick={() => setShowContext(!showContext)}
            title="Toggle retrieved context"
            data-testid="agent-context-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </button>
          <button
            className="agent-action-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Settings"
            data-testid="agent-settings-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          <button
            className="agent-action-btn danger"
            onClick={clearHistory}
            title="Clear chat"
            data-testid="agent-clear-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Checkpoints Panel (collapsible) */}
      {showCheckpoints && state.checkpoints.length > 0 && (
        <div className="agent-checkpoints-panel" data-testid="agent-checkpoints-panel">
          <div className="agent-checkpoints-header">
            <span>Code Changes</span>
            <span className="checkpoints-count">{revertableCheckpoints.length} revertable</span>
          </div>
          {state.checkpoints.map((checkpoint) => (
            <div
              key={checkpoint.id}
              className={`checkpoint-item ${checkpoint.reverted ? 'reverted' : ''}`}
              data-testid="checkpoint-item"
            >
              <div className="checkpoint-info">
                <span className="checkpoint-filename">{checkpoint.filename}</span>
                <span className="checkpoint-time">
                  {new Date(checkpoint.createdAt).toLocaleTimeString()}
                </span>
              </div>
              <button
                className="checkpoint-revert-btn"
                onClick={() => revertCheckpoint(checkpoint)}
                disabled={checkpoint.reverted}
                title={checkpoint.reverted ? 'Already reverted' : 'Revert this change'}
              >
                {checkpoint.reverted ? '✓ Reverted' : '↩ Undo'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Settings Panel (collapsible) */}
      {showSettings && (
        <div className="agent-settings-panel" data-testid="agent-settings-panel">
          <div className="agent-setting">
            <label>Model</label>
            <select
              value={state.settings.model}
              onChange={(e) => updateSettings({ model: e.target.value })}
              data-testid="agent-model-select"
            >
              <option value="gpt-5-nano">GPT-5 Nano</option>
              <option value="gpt-5-mini">GPT-5 Mini</option>
              <option value="gpt-5">GPT-5</option>
              <option value="gpt-4.1">GPT-4.1</option>
              <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
              <option value="gpt-4.1-nano">GPT-4.1 Nano</option>
              <option value="gpt-4o">GPT-4o</option>
              <option value="gpt-4o-mini">GPT-4o Mini</option>
              <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
            </select>
          </div>
          <div className="agent-setting">
            <label>Temperature: {state.settings.temperature}</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={state.settings.temperature}
              onChange={(e) => updateSettings({ temperature: parseFloat(e.target.value) })}
            />
          </div>
          <div className="agent-setting checkbox">
            <label>
              <input
                type="checkbox"
                checked={state.settings.autoApplyCode}
                onChange={(e) => updateSettings({ autoApplyCode: e.target.checked })}
              />
              Auto-apply generated code
            </label>
          </div>
        </div>
      )}

      {/* Retrieved Context Panel (collapsible) */}
      {showContext && state.lastRetrievedContext.length > 0 && (
        <div className="agent-context-panel" data-testid="agent-context-panel">
          <div className="agent-context-header">Retrieved Context</div>
          {state.lastRetrievedContext.map((ctx) => (
            <div key={ctx.id} className="agent-context-item" data-testid="agent-context-item">
              <div className="context-source">
                <span className={`source-badge ${ctx.source}`}>{ctx.source}</span>
                <span className="context-path">{ctx.path}</span>
                <span className="context-relevance">{(ctx.relevance * 100).toFixed(0)}%</span>
              </div>
              <pre className="context-snippet">{ctx.snippet}</pre>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="agent-chat-messages" data-testid="agent-chat-messages">
        {state.messages.filter(m => m.role !== 'system').length === 0 && (
          <div className="agent-empty-state" data-testid="agent-empty-state">
            <div className="agent-empty-icon"><IconChat size="1em" /></div>
            <p>Ask me to help with your sandbox!</p>
            <div className="agent-suggestions" data-testid="agent-suggestions">
              <button
                data-testid="suggestion-jump"
                onClick={() => sendMessage('How do I add a jump mechanic?')}
              >
                Add jump mechanic
              </button>
              <button
                data-testid="suggestion-particle"
                onClick={() => sendMessage('Create a particle system')}
              >
                Create particles
              </button>
              <button
                data-testid="suggestion-netcode"
                onClick={() => sendMessage('Explain the netcode template')}
              >
                Explain netcode
              </button>
            </div>
          </div>
        )}

        {/* MessageBubble takes only `message`; the apply-from-chat button was
            removed from CodeBlockView (it renders copy-only now), so the
            `onApplyCode` prop this used to pass went nowhere. */}
        {state.messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {/* Linear tool-call timeline (deep-agent strategy only) — renders
            itself only when toolTimeline is non-empty; sits above the tree. */}
        {state.v2Tree && state.v2Tree.rootId && <AgentToolTimeline />}

        {/* v2 recursive-tree pipeline view (when v2 is active or has run). */}
        {state.v2Tree && state.v2Tree.rootId && <V2PipelineTreeView />}

        {/* Legacy flat pipeline status indicator (still shown for v1/legacy). */}
        {showPipelineStatus && !state.v2Tree && (
          <PipelineStatusIndicator
            step={pipelineStatus.step}
            message={pipelineStatus.message}
            tool={pipelineStatus.tool}
            modifiedFiles={pipelineStatus.modifiedFiles}
            completedSteps={pipelineStatus.completedSteps || []}
            liveTestResults={pipelineStatus.liveTestResults}
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form className="agent-chat-input-container" onSubmit={handleSubmit} data-testid="agent-input-form">
        <textarea
          ref={inputRef}
          className="agent-chat-input"
          placeholder="Ask the agent..."
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={state.isProcessing}
          rows={1}
          data-testid="agent-chat-input"
        />
        <button
          type="submit"
          className="agent-send-btn"
          disabled={!inputValue.trim() || state.isProcessing}
          data-testid="agent-send-btn"
        >
          {state.isProcessing ? (
            <span className="send-spinner" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}
